import { useState, useEffect } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdAdd, MdDelete, MdToggleOn, MdToggleOff, MdClose } from 'react-icons/md';
import DragDropImageUpload from '../components/DragDropImageUpload';

export default function Ads() {
  const [ads, setAds] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({
    ad_type: 'sponsored_card', title: '', summary: '', image_url: '',
    link_url: '', region: 'all_india', is_active: false,
    starts_at: '', ends_at: '', impression_cap: '', media: [],
  });

  const fetchAds = async () => {
    try {
      const res = await api.get('/admin/ads');
      setAds(res.data.data);
    } catch (err) {
      toast.error('Failed to load ads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAds();
    api.get('/cities').then(res => setCities(res.data.data)).catch(() => { });
  }, []);

  const set = (key, val) => {
    setForm(prev => ({ ...prev, [key]: val }));
    setFormErrors(prev => ({ ...prev, [key]: undefined }));
  };

  const validateForm = () => {
    const errs = {};
    if (!form.image_url.trim() && (!form.media || form.media.length === 0)) {
      errs.image_url = 'At least one Ad image or media is required';
    }
    if (form.starts_at && form.ends_at && new Date(form.ends_at) <= new Date(form.starts_at)) {
      errs.ends_at = 'End date must be after start date';
    }
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      if (formErrors.image_url) toast.error(formErrors.image_url);
      return;
    }
    try {
      const payload = { ...form };
      if (!payload.starts_at) delete payload.starts_at;
      if (!payload.ends_at) delete payload.ends_at;
      if (!payload.impression_cap) delete payload.impression_cap;
      else payload.impression_cap = parseInt(payload.impression_cap);
      await api.post('/admin/ads', payload);
      toast.success('Ad created');
      setShowForm(false);
      setForm({ ad_type: 'sponsored_card', title: '', summary: '', image_url: '', link_url: '', region: 'all_india', is_active: false, starts_at: '', ends_at: '', impression_cap: '', media: [] });
      setFormErrors({});
      fetchAds();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  const toggleAd = async (id) => {
    try {
      await api.patch(`/admin/ads/${id}/toggle`);
      fetchAds();
    } catch (err) {
      toast.error('Toggle failed');
    }
  };

  const confirmDeleteAd = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/ads/${deleteModal.id}`);
      toast.success('Ad deleted');
      setDeleteModal(null);
      fetchAds();
    } catch (err) {
      toast.error('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const getCtr = (ad) => ad.impression_count > 0
    ? ((ad.tap_count / ad.impression_count) * 100).toFixed(2) + '%'
    : '0%';

  const getStatus = (ad) => {
    if (!ad.is_active) return 'Inactive';
    if (ad.impression_cap && ad.impression_count >= ad.impression_cap) return 'Capped';
    if (ad.ends_at && new Date(ad.ends_at) < new Date()) return 'Ended';
    if (ad.starts_at && new Date(ad.starts_at) > new Date()) return 'Scheduled';
    return 'Active';
  };

  if (loading) return <div className="page-loader">Loading...</div>;

  return (
    <div className="page">
      <div className="page-header">
        <h2>Ads Manager</h2>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? <><MdClose /> Cancel</> : <><MdAdd /> Create Ad</>}
        </button>
      </div>

      {showForm && (
        <form className="card form-card" onSubmit={handleCreate}>
          <div className="form-row">
            <div className="form-group">
              <label>Ad Type</label>
              <div className="radio-group">
                <label className={`radio-card ${form.ad_type === 'sponsored_card' ? 'selected' : ''}`}>
                  <input type="radio" value="sponsored_card" checked={form.ad_type === 'sponsored_card'}
                    onChange={() => set('ad_type', 'sponsored_card')} /> Sponsored Card
                </label>
                <label className={`radio-card ${form.ad_type === 'fullscreen' ? 'selected' : ''}`}>
                  <input type="radio" value="fullscreen" checked={form.ad_type === 'fullscreen'}
                    onChange={() => set('ad_type', 'fullscreen')} /> Full Screen
                </label>
              </div>
            </div>
          </div>

          {form.ad_type === 'sponsored_card' && (
            <>
              <div className="form-group">
                <label>Title</label>
                <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Ad title" />
              </div>
              <div className="form-group">
                <label>Summary</label>
                <textarea value={form.summary} onChange={e => set('summary', e.target.value)} placeholder="Ad summary" rows={3} />
              </div>
            </>
          )}

          <div className="form-group border-t border-gray-200 pt-4 mt-4">
            <label>Ad Media Gallery (Images & Videos)</label>
            <div className="flex-row gap-4 flex-wrap mb-sm">
              {(form.media || []).map((m, i) => (
                <div key={i} style={{ position: 'relative', width: 80, height: 80 }}>
                  {m.type === 'video' ? (
                    <video src={m.url} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                  ) : (
                    <img src={m.url} alt={`ad-media-${i}`} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                  )}
                  <button type="button" onClick={() => set('media', form.media.filter((_, index) => index !== i))} style={{ position: 'absolute', top: -5, right: -5, background: 'red', color: 'white', borderRadius: '50%', width: 20, height: 20, border: 'none', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>X</button>
                </div>
              ))}
              <div
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'image/*,video/mp4';
                  input.onchange = async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const isVideo = file.type.startsWith('video');
                    const formData = new FormData();
                    formData.append('file', file);
                    setUploading(true);
                    try {
                      const endpoint = isVideo ? '/admin/upload/video' : '/admin/upload/ad-image';
                      const res = await api.post(endpoint, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                      });
                      const newMedia = { url: res.data.data.url, type: isVideo ? 'video' : 'image' };

                      const updatedMedia = [...(form.media || []), newMedia];
                      set('media', updatedMedia);

                      if (!form.image_url && !isVideo) {
                        set('image_url', res.data.data.url);
                      }
                      toast.success(isVideo ? 'Video uploaded' : 'Image uploaded');
                    } catch (err) {
                      toast.error('Upload failed');
                    } finally {
                      setUploading(false);
                    }
                  };
                  input.click();
                }}
                style={{ width: 80, height: 80, border: '2px dashed #ccc', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#fafafa' }}
              >
                {uploading ? <span style={{ fontSize: 12, color: '#999' }}>...</span> : <span style={{ fontSize: 24, color: '#999' }}>+</span>}
              </div>
            </div>
            <p className="text-muted text-xs">Upload images and MP4 videos to create a carousel for this ad.</p>
            {formErrors.image_url && <span className="field-error">{formErrors.image_url}</span>}
          </div>
          <div className="form-group">
            <label>Link URL (optional)</label>
            <input value={form.link_url} onChange={e => set('link_url', e.target.value)} placeholder="https://..." />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <select value={form.region} onChange={e => set('region', e.target.value)}>
                <option value="all_india">All Cities</option>
                {cities.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Impression Cap</label>
              <input type="number" value={form.impression_cap} onChange={e => set('impression_cap', e.target.value)} placeholder="Unlimited" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Start Date</label>
              <input type="datetime-local" value={form.starts_at} onChange={e => set('starts_at', e.target.value)} />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input type="datetime-local" value={form.ends_at} onChange={e => set('ends_at', e.target.value)} />
              {formErrors.ends_at && <span className="field-error">{formErrors.ends_at}</span>}
            </div>
          </div>

          <label className="checkbox-label">
            <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
            Active immediately
          </label>

          <button type="submit" className="btn btn-primary mt-md">Create Ad</button>
        </form>
      )}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Type</th>
              <th className="hide-mobile">Region</th>
              <th>Status</th>
              <th className="hide-mobile">Impressions</th>
              <th className="hide-mobile">Taps</th>
              <th className="hide-mobile">CTR</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {ads.map(ad => (
              <tr key={ad.id}>
                <td>
                  <span className="badge">{ad.ad_type === 'sponsored_card' ? 'Sponsored' : 'Fullscreen'}</span>
                  {ad.title && <p className="cell-sub">{ad.title}</p>}
                </td>
                <td className="hide-mobile">{ad.region?.replace('_', ' ')}</td>
                <td>
                  <span className={`badge ${getStatus(ad) === 'Active' ? 'badge-live' : getStatus(ad) === 'Inactive' ? 'badge-expired' : ''}`}>
                    {getStatus(ad)}
                  </span>
                </td>
                <td className="hide-mobile">{ad.impression_count}{ad.impression_cap ? `/${ad.impression_cap}` : ''}</td>
                <td className="hide-mobile">{ad.tap_count}</td>
                <td className="hide-mobile">{getCtr(ad)}</td>
                <td>
                  <div className="action-btns">
                    <button className="btn btn-sm btn-ghost" onClick={() => toggleAd(ad.id)}>
                      {ad.is_active ? <MdToggleOn className="text-green" /> : <MdToggleOff />}
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => setDeleteModal(ad)}><MdDelete /></button>
                  </div>
                </td>
              </tr>
            ))}
            {ads.length === 0 && <tr><td colSpan="7" className="empty-row">No ads yet</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="mobile-cards">
        {ads.map(ad => (
          <div key={ad.id} className="mobile-card">
            <div className="mobile-card-header">
              <span className={`badge ${getStatus(ad) === 'Active' ? 'badge-live' : 'badge-expired'}`}>{getStatus(ad)}</span>
              <span className="badge">{ad.ad_type === 'sponsored_card' ? 'Sponsored' : 'Fullscreen'}</span>
            </div>
            {ad.title && <h4>{ad.title}</h4>}
            <div className="mobile-card-stats">
              <span>{ad.impression_count} impr</span>
              <span>{ad.tap_count} taps</span>
              <span>CTR: {getCtr(ad)}</span>
            </div>
            <div className="action-btns">
              <button className="btn btn-sm btn-ghost" onClick={() => toggleAd(ad.id)}>
                {ad.is_active ? 'Deactivate' : 'Activate'}
              </button>
              <button className="btn btn-sm btn-danger" onClick={() => setDeleteModal(ad)}><MdDelete /> Delete</button>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Ad</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this ad?</p>
              {deleteModal.title && <p className="text-muted" style={{ marginTop: 8 }}>"{deleteModal.title}"</p>}
              <p className="text-muted" style={{ marginTop: 8 }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger-solid" onClick={confirmDeleteAd} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
